import type { BatchItem } from '@rpldy/shared';
import {
  type PreSendData,
  useAbortItem,
  useRequestPreSend,
} from '@rpldy/uploady';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { APP_ID } from '@/constants/constant';
import { createSpeechUploadQueryCreator } from '@/query/createSpeechUploadQueryCreator';
import { endSpeechUploadMutationCreator } from '@/query/mutations/endSpeechUploadMutationCreator';
import { userProfileQueryCreator } from '@/query/userProfileQueryCreator';
import { importS3SuccessEvent } from '../tracking/tracking';
import type {
  ImportQuotas,
  UploadItem,
  UploadModalContextData,
} from '../types/types';
import {
  extractUploadParams,
  extractUploadQuotas,
  parseXmlResponse,
} from '../utils/utils';
import { useConversationStatus } from './useConversationStatus';

export function useCreateSpeechUpload() {
  const { data: user } = useQuery(userProfileQueryCreator());
  const userId = String(user?.user.id);

  const { data, error, refetch } = useQuery(
    createSpeechUploadQueryCreator(userId, { enabled: false }),
  );

  return { data, error, refetch };
}

export function useUploadPreSend(setQuota: (quota: ImportQuotas) => void) {
  const { refetch } = useCreateSpeechUpload();

  useRequestPreSend(async ({ options }: PreSendData) => {
    const { data, error } = await refetch();

    const speechUploadParams = extractUploadParams(data?.data);
    const freshQuota = extractUploadQuotas(data, error);
    setQuota(freshQuota);

    // If speech upload params are not available,
    // return false to prevent the upload
    if (!speechUploadParams) {
      return false;
    }

    return {
      options: {
        ...options,
        destination: {
          ...(options?.destination || {}),
          url: speechUploadParams.url,
        },
        params: {
          ...(options?.params || {}),
          ...speechUploadParams.fields,
        },
        withCredentials: false,
        sendWithFormData: true,
      },
    };
  });
}

export function useSpeechTranscriptionStatus() {
  const { statusMap, updateStatusWithOtid } = useConversationStatus();

  const fallbackTranscriptionStatus = useMemo(
    () => ({
      isTranscribing: false,
      isAudioProcessing: false,
      isTranscriptionComplete: false,
      transcriptionProgress: 0,
      remainingMinutes: 0,
    }),
    [],
  );

  const getSpeechTranscriptionStatus = useCallback(
    (otid?: string) => {
      if (!otid) return null;

      const entry = statusMap.get(otid);
      const status = entry?.status;

      if (!status) return fallbackTranscriptionStatus;

      return {
        isTranscribing: status.asr === 'processing',
        isAudioProcessing: status.audio_process === 'processing',
        isTranscriptionComplete: status.asr === 'finished',
        transcriptionProgress: status.asr_percentage_done ?? 0,
        remainingMinutes: status.asr_remaining_minutes ?? 0,
      };
    },
    [statusMap, fallbackTranscriptionStatus],
  );

  const initializeSpeech = useCallback(
    (otid: string, title: string) => {
      updateStatusWithOtid(otid, title);
    },
    [updateStatusWithOtid],
  );

  return {
    getSpeechTranscriptionStatus,
    initializeSpeech,
  };
}

// Hook for processing uploaded item
export function usePostUploadProcessing() {
  const endSpeechUpload = useMutation(endSpeechUploadMutationCreator({}));

  const processUploadedItem = async (
    item: BatchItem,
    contextData: UploadModalContextData,
  ) => {
    const { bucket, key } = parseXmlResponse(item);

    if (!bucket || !key) return null;

    const params =
      contextData?.type === 'folder'
        ? { bucket, key, appid: APP_ID, folder_id: contextData.id ?? undefined } // folder
        : contextData?.type === 'group'
          ? {
              bucket,
              key,
              appid: APP_ID,
              group_id: contextData.id ?? undefined,
            } // group
          : { bucket, key, appid: APP_ID }; // home

    const result = await endSpeechUpload.mutateAsync(params);

    if (result) {
      importS3SuccessEvent(item, key);
      return result;
    }

    return null;
  };

  return { processUploadedItem };
}

export function useCancelAll() {
  const abort = useAbortItem();

  const cancelAll = useCallback(
    (items: UploadItem[]) => {
      items.forEach((item) => {
        abort(item.id);
      });
    },
    [abort],
  );

  return cancelAll;
}
